<template>
    <div class="grid w-full">
        <div class="col flex-column md:flex-row flex justify-content-between align-items-center">
            <div class="flex-order-2 md:flex-order-1">
                <Inplace
                    ref="inplace"
                    :pt="{
                        root: {class: 'align-self-start'},
                        display: {class: 'inline-block', style: 'margin-left: -12px;'}
                    }"
                >
                    <template #display>
                        <div class="flex align-items-center gap-2">
                            <h3>{{ getTitle }}</h3>
                            <i class="pi pi-pencil report_title__icon_edit"></i>
                        </div>
                    </template>
                    <template #content>
                        <div class="flex flex-wrap-nowrap align-items-center report_title__field_container gap-2">
                            <AdaptersLoader :loading="loading" />
                            <InputText
                                v-model="model.title"
                                @keyup.enter="onRenameProject"
                                @keyup.esc="onClose"
                                @focusout="onClose"
                                autofocus
                                class="report_title__input_edit"
                            />
                            <i class="pi pi-pencil report_title__icon_edit"></i>
                        </div>
                    </template>
                </Inplace>
                <div class="flex align-options-center gap-1 align-items-center">
                    <span
                        class="text-color-secondary"
                        v-html="$t('lang.wordstat.report.subtitle', [
                            getRegionCaption,
                            $t('lang.wordstat.report.source_type_' + WordstatHelper.getSourceSlug(this.source))
                        ])"
                    ></span>
                    <i
                        v-if="!isWordstatRegion"
                        class="pi pi-exclamation-circle text-orange-400 text-sm"
                        :title="$t('lang.wordstat.missing_region_data')"
                    ></i>
                </div>
            </div>
            <div class="flex gap-2 flex-order-1 md:flex-order-2 flex-column sm:flex-row w-full sm:w-auto">
                <Button
                    :label="$t('lang.wordstat.report.add_keys')"
                    size="small"
                    icon-pos="right"
                    @click="onShowAdd"
                    :class="{'opacity-50': isDisabledActions}"
                />
                <Button
                    :label="$t('lang.wordstat.report.delete_keys')"
                    size="small"
                    icon-pos="right"
                    icon="pi pi-angle-down"
                    @click="toggleDelete"
                    :class="{'opacity-50': isDisabledActions}"
                    :pt="{
                        icon: { class: 'absolute sm:static', style: 'right: 1rem' }
                    }"
                />
                <Button
                    :label="$t('lang.wordstat.report.update')"
                    size="small"
                    @click="onShowUpdate"
                />
                <WordstatUpdateProject @onAgree="onUpdateStart" />
                <WordstatUpdateWords @onAgree="onUpdateStart" />
                <WordstatImportFromArea
                    :id="id"
                    :clusterUid="clusterUid"
                    :clusterName="clusterName"
                    :monitoringId="monitoringId"
                    :monitoringName="monitoringName"
                    :source="source"
                />
                <TieredMenu ref="tieredMenuDelete" :model="tieredMenu.delete" popup />
                <WordstatDeleteFromArea
                    :id="id"
                    :clusterUid="clusterUid"
                    :clusterName="clusterName"
                    :monitoringId="monitoringId"
                    :monitoringName="monitoringName"
                />
                <WordstatDeleteMinusWords
                    :id="id"
                    :clusterUid="clusterUid"
                    :clusterName="clusterName"
                    :monitoringId="monitoringId"
                    :monitoringName="monitoringName"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Inplace from "primevue/inplace";
import InputText from "primevue/inputtext";
import yandexRegionsList from "../../../../../resources/yandex-regions.json";
import {ArrayHelper} from "../../../helpers/ArrayHelper";
import Button from "primevue/button";
import TieredMenu from "primevue/tieredmenu";
import {Listeners} from "../../../constants/listeners";
import WordstatImportFromArea from "./ImportFromArea.vue";
import WordstatDeleteFromArea from "./DeleteFromArea.vue";
import WordstatDeleteMinusWords from "./DeleteMinusWords.vue";
import WordstatApi from "../../../api/WordstatApi";
import Alerts from "../../../mixins/Alerts";
import AdaptersLoader from "../../../component/adapters/Loader.vue";
import WordstatUpdateProject from "./UpdateProject.vue";
import {WordstatHelper} from "../helpers/WordstatHelper";
import WordstatUpdateWords from "./UpdateWords.vue";

export default {
    name: "WordstatReportTitle",
    mixins: [Alerts],
    props: {
        id: {
            type: Number,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        regionId: {
            type: Number,
            required: true,
        },
        clusterUid: {
            type: String,
            required: false,
            default: null,
        },
        clusterName: {
            type: String,
            required: false,
            default: null,
        },
        monitoringId: {
            type: Number,
            required: false,
            default: null,
        },
        monitoringName: {
            type: String,
            required: false,
            default: null,
        },
        source: {
            type: Number,
            required: true,
        },
        wordsCount: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            model: {
                title: this.title
            },
            currentTitle: null,
            tieredMenu: {
                delete: [
                    {
                        label: this.$t('lang.wordstat.report.delete_words_button'),
                        command: () => this.$emitter.emit(Listeners.WORDSTAT_POPUP_DELETE_FROM_AREA_OPEN),
                    },
                    {
                        label: this.$t('lang.wordstat.report.delete_minus_words_button'),
                        command: () => this.$emitter.emit(Listeners.WORDSTAT_POPUP_DELETE_MINUS_WORDS_OPEN),
                    },
                ],
            }
        }
    },
    mounted() {
        this.currentTitle = this.title;
    },
    computed: {
        WordstatHelper() {
            return WordstatHelper;
        },
        getTitle() {
            return this.model.title;
        },
        getRegionCaption() {
            const region = ArrayHelper.getObjectByAttrValue(yandexRegionsList, 'id', this.regionId);
            return region.title;
        },
        isWordstatRegion() {
            const region = ArrayHelper.getObjectByAttrValue(yandexRegionsList, 'id', this.regionId);
            return region.wordstat;
        },
        isDisabledActions() {
            return !!(this.clusterUid || this.monitoringId);
        },
    },
    methods: {
        onRenameProject() {
            this.loading = true;
            WordstatApi
                .updateProjectName({projectId: this.id, name: this.model.title})
                .then(() => {
                    this.currentTitle = this.model.title;
                    this.$emitter.emit(Listeners.BLOCK_BREADCRUMBS_LAST_ITEM_RENAME, this.model.title);
                    this.$refs.inplace.close();
                })
                .catch(error => this.alertError(error))
                .finally(() => this.loading = false);
        },
        onClose() {
            this.model.title = this.currentTitle;
            this.$refs.inplace.close();
        },
        onShowAdd() {
            this.$emitter.emit(Listeners.WORDSTAT_POPUP_IMPORT_FROM_AREA)
        },
        onShowUpdate() {
            this.$emitter.emit(Listeners.WORDSTAT_POPUP_UPDATE_PROJECT_OPEN, {
                id: this.id,
                source: this.source,
                wordsCount: this.wordsCount,
            });
        },
        toggleDelete(event) {
            this.$refs.tieredMenuDelete.toggle(event);
        },
        onUpdateStart() {
            location.reload();
        },
    },
    components: {
        WordstatUpdateWords,
        AdaptersLoader,
        WordstatImportFromArea,
        WordstatDeleteFromArea,
        Inplace,
        InputText,
        Button,
        TieredMenu,
        WordstatUpdateProject,
        WordstatDeleteMinusWords,
    }
}
</script>

<style scoped>
.report_title__field_container {
    padding: 1px 0 0;
}
.report_title__input_edit {
    font-size: 24px;
    margin: 1px 0 0;
}
.report_title__icon_edit {
    color: var(--secondary-color);
}
</style>
